import { useEffect, useMemo, useRef, useState } from 'react'
import { Maximize2, Plus, RefreshCw } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import {
  Button,
  Drawer,
  EmptyState,
  QueryErrorState,
  groupRows,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarDivider,
  ToolbarLeft,
  ToolbarRight,
  useTableGrouping,
  ViewToggle,
  type GroupAccent,
  type TableGroup,
} from '@/components/ui'
import { ScopeChip } from '@/shell/components/ScopeChip'
import { useQuotes } from '../api/queries'
import type { QuoteStatus, QuoteSummary } from '../api/types'
import { QuoteDetail } from '../components/QuoteDetail'
import { QuotePipelineBoard } from '../components/QuotePipelineBoard'
import { SendQuoteDialog } from '../components/SendQuoteDialog'
import {
  DEFAULT_FILTERS,
  QuotesFilterRail,
  type QuotesClientFilters,
} from '../components/QuotesFilterRail'
import { QuotesTable } from '../components/QuotesTable'
import { LABEL_BY_STATUS, VARIANT_BY_STATUS } from '../components/quoteStatusMeta'

const QUOTE_STATUS_ORDER: readonly QuoteStatus[] = [
  'draft',
  'in_review',
  'revise_pricing',
  'approved',
  'sent',
  'customer_approved',
  'customer_rejected',
]

function quoteStatusAccent(status: string): GroupAccent {
  const v = VARIANT_BY_STATUS[status as QuoteStatus]
  if (v === 'success' || v === 'warning' || v === 'danger') return v
  return 'neutral'
}

/**
 * Quotes — Sprint 12b restructure. Mirrors the CofA Management UI/UX:
 *   - Inline header band (title + subtitle + MetricStrip + primary action)
 *   - Collapsible filter rail on the left (status chips, customer/
 *     submitter contains, archived toggle)
 *   - Toolbar: SearchInput + ViewToggle (Table / Kanban) + Group-by Select +
 *     count + refresh
 *   - Body: Table view (default) with sortable columns + per-row ⋯ menu,
 *     OR Kanban view (Approval Queue) with rejected revision flow.
 *   - Detail opens in a wide right-side Drawer (non-modal, click-out
 *     dismissable).
 */

type ViewMode = 'table' | 'kanban'
type GroupBy = 'none' | 'customer' | 'status' | 'salesperson'

export function QuotesPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filters, setFilters] = useState<QuotesClientFilters>(DEFAULT_FILTERS)
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const { groupBy, setGroupBy, collapsedGroups, toggleGroup } =
    useTableGrouping('quotes')
  const [filterRailCollapsed, setFilterRailCollapsed] = useState(false)
  const [drawerQuoteId, setDrawerQuoteId] = useState<number | null>(null)
  const [sendDialogQuote, setSendDialogQuote] = useState<QuoteSummary | null>(
    null,
  )
  const searchRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(id)
  }, [search])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const { data, isLoading, isFetching, isError, error, refetch } = useQuotes({
    q: debouncedSearch,
    status: filters.status === 'all' ? null : (filters.status as QuoteStatus),
    submittedBy: filters.submitterQuery.trim() || null,
    includeArchived: filters.includeArchived,
  })

  const allQuotes = useMemo<QuoteSummary[]>(() => data?.quotes ?? [], [data])

  // Client-side customer filter: the summary endpoint now returns
  // `customer_name` resolved from Odoo, so we substring-match against
  // both the name and the id (numeric or `customer #N` typed by users
  // who saw the legacy placeholder).
  const filtered = useMemo<QuoteSummary[]>(() => {
    const raw = filters.customerQuery.trim().toLowerCase()
    const needle = raw.replace(/^customer\s*#?\s*/i, '').trim()
    if (!needle) return allQuotes
    return allQuotes.filter((q) => {
      const name = (q.customer_name ?? '').toLowerCase()
      return (
        name.includes(needle) ||
        String(q.customer_id).includes(needle)
      )
    })
  }, [allQuotes, filters.customerQuery])

  // Table-view grouping. Status renders all 7 stages (empty included);
  // customer/salesperson are derived (only present keys, alphabetical).
  const groups = useMemo<TableGroup<QuoteSummary>[] | undefined>(() => {
    if (viewMode !== 'table' || groupBy === 'none') return undefined
    if (groupBy === 'status') {
      return groupRows(filtered, {
        keyOf: (q) => q.status,
        groupOrder: QUOTE_STATUS_ORDER,
        labelOf: (k) => LABEL_BY_STATUS[k as QuoteStatus] ?? k,
        accentOf: (k) => quoteStatusAccent(k),
      })
    }
    if (groupBy === 'customer') {
      return groupRows(filtered, {
        keyOf: (q) => q.customer_name || `customer #${q.customer_id}`,
        labelOf: (k) => (k === '__none__' ? '(no customer)' : k),
      })
    }
    // salesperson
    return groupRows(filtered, {
      keyOf: (q) => q.submitted_by ?? '',
      labelOf: (k) => (k === '__none__' ? '(no salesperson)' : k),
    })
  }, [viewMode, groupBy, filtered])

  // Counts for the metric strip (always over the full live list, not
  // the filtered set — matches CofA Management's stats-band semantics).
  const counts = useMemo(() => {
    const out = {
      total: allQuotes.length,
      drafts: 0,
      in_review: 0,
      approved_or_sent: 0,
      customer_approved: 0,
    }
    for (const q of allQuotes) {
      if (q.status === 'draft' || q.status === 'revise_pricing')
        out.drafts += 1
      else if (q.status === 'in_review') out.in_review += 1
      else if (q.status === 'approved' || q.status === 'sent')
        out.approved_or_sent += 1
      else if (q.status === 'customer_approved') out.customer_approved += 1
    }
    return out
  }, [allQuotes])

  // Sprint 12d — New quote always opens the full-page route, never
  // the Drawer. The Drawer is only for previewing existing quotes.
  function handleNew() {
    navigate({ to: '/quotes/new' })
  }
  function handleSelect(q: QuoteSummary) {
    setDrawerQuoteId(q.id)
  }
  function handleSend(q: QuoteSummary) {
    setSendDialogQuote(q)
  }
  function closeDrawer() {
    setDrawerQuoteId(null)
  }

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="Quotes"
        description="Sales quotes — middleware-resident lifecycle. Becomes an Odoo sale.order only when a customer PO is recorded and converted."
      >
        <ScopeChip />
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard
            variant="inline"
            label="Total quotes"
            value={counts.total.toLocaleString()}
          />
          <MetricCard
            variant="inline"
            label="Drafts"
            value={counts.drafts.toLocaleString()}
            valueTone={counts.drafts > 0 ? 'default' : 'default'}
          />
          <MetricCard
            variant="inline"
            label="Pricing In Review"
            value={counts.in_review.toLocaleString()}
            valueTone={counts.in_review > 0 ? 'warning' : 'default'}
          />
          <MetricCard
            variant="inline"
            label="Approved / Sent"
            value={counts.approved_or_sent.toLocaleString()}
          />
          <MetricCard
            variant="inline"
            label="Customer Approved"
            value={counts.customer_approved.toLocaleString()}
          />
        </MetricStrip>
        <Button
          variant="primary"
          size="sm"
          onClick={handleNew}
        >
          <Plus size={14} />
          New quote
        </Button>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1">
        <QuotesFilterRail
          filters={filters}
          onChange={setFilters}
          collapsed={filterRailCollapsed}
          onToggleCollapsed={() => setFilterRailCollapsed((v) => !v)}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <Toolbar>
            <SearchInput
              ref={searchRef}
              containerClassName="flex-1 max-w-none"
              placeholder="Search quote #, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search"
            />
          </Toolbar>
          <Toolbar>
            <ToolbarLeft>
              <ViewToggle
                aria-label="View mode"
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { value: 'table', label: 'Table' },
                  { value: 'kanban', label: 'Kanban' },
                ]}
              />
              {viewMode === 'table' ? (
                <>
                  <ToolbarDivider />
                  <label className="flex items-center gap-1.5 text-role-section-label">
                    Group
                    <Select
                      aria-label="Group by"
                      value={groupBy}
                      onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                    >
                      <option value="none">None</option>
                      <option value="customer">Customer</option>
                      <option value="status">Status</option>
                      <option value="salesperson">Salesperson</option>
                    </Select>
                  </label>
                </>
              ) : null}
            </ToolbarLeft>
            <ToolbarRight>
              <ToolbarCount>
                {filtered.length.toLocaleString()} of{' '}
                {allQuotes.length.toLocaleString()}
              </ToolbarCount>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Refresh"
                loading={isFetching && !isLoading}
                onClick={() => refetch()}
              >
                <RefreshCw size={14} />
              </Button>
            </ToolbarRight>
          </Toolbar>

          <div className="flex-1 min-h-0 overflow-hidden">
            {isError ? (
              <QueryErrorState
                error={error}
                onRetry={() => void refetch()}
                subject="quotes"
              />
            ) : isLoading ? (
              <div className="py-8 text-center text-sm text-tertiary">
                Loading quotes…
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                title="No quotes match"
                description={
                  allQuotes.length === 0
                    ? "No quotes yet. Click New quote to author one, or use CARLA's create_quote_draft tool."
                    : 'Try clearing some filters.'
                }
                action={
                  allQuotes.length === 0 ? (
                    <Button variant="primary" size="sm" onClick={handleNew}>
                      <Plus size={14} /> New quote
                    </Button>
                  ) : null
                }
              />
            ) : viewMode === 'kanban' ? (
              <div className="h-full overflow-auto p-4">
                <QuotePipelineBoard
                  quotes={filtered}
                  onSelect={handleSelect}
                  selectedId={
                    drawerQuoteId !== null
                      ? String(drawerQuoteId)
                      : undefined
                  }
                />
              </div>
            ) : (
              <div className="h-full overflow-auto p-4">
                <QuotesTable
                  rows={filtered}
                  groups={groups}
                  collapsedGroups={collapsedGroups}
                  onToggleGroup={toggleGroup}
                  onSelect={handleSelect}
                  onSend={handleSend}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail drawer — preview only (existing quotes). New + revise
          flows navigate full-page (Sprint 12d). The "Open full"
          header action navigates here for any deeper editing. */}
      <Drawer
        open={drawerQuoteId !== null}
        onOpenChange={(next) => {
          if (!next) closeDrawer()
        }}
        size="wide"
        title={(() => {
          const q = allQuotes.find((qt) => qt.id === drawerQuoteId)
          return q ? q.quote_number : 'Quote'
        })()}
        meta={(() => {
          const q = allQuotes.find((qt) => qt.id === drawerQuoteId)
          if (!q) return null
          const label = q.customer_name || `customer #${q.customer_id}`
          return `${label} · ${q.line_count} line${q.line_count === 1 ? '' : 's'}`
        })()}
        headerActions={
          drawerQuoteId !== null ? (
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              aria-label="Open full screen"
              title="Open full screen"
              onClick={() => {
                const id = drawerQuoteId
                closeDrawer()
                navigate({
                  to: '/quotes/$quoteId',
                  params: { quoteId: String(id) },
                })
              }}
            >
              <Maximize2 size={14} />
            </Button>
          ) : null
        }
      >
        {drawerQuoteId !== null ? (
          <QuoteDetail quoteId={drawerQuoteId} />
        ) : null}
      </Drawer>

      <SendQuoteDialog
        open={sendDialogQuote !== null}
        quoteId={sendDialogQuote?.id ?? null}
        quoteNumber={sendDialogQuote?.quote_number ?? null}
        onClose={() => setSendDialogQuote(null)}
      />
    </div>
  )
}

